// pages/admin/wallet/WalletAnalyticsPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useCompanyWallet } from '../../../context/CompanyWalletContext';
import { useStoreSettings } from '../../../context/StoreSettingsContext';
import { WalletTransaction, WalletTransactionType, WalletTransactionStatus } from '../../../types/stores';
import { renderAnalyticsTab,   } from './WalletTabs';
import { renderOverviewTab } from './OverviewTab';
import { renderTransactionsTab } from './TransactionsTab';
import {
  handleFilterSearch,
  renderPagination,
  handleSort,
  handleResetFilters
} from "./helpers/walletTableHelpers";

// SVG Icons
const StatsIcons = {
  refresh: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  chevronLeft: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  ),
  chevronRight: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  ),
};

interface DateRange {
  startDate: Date;
  endDate: Date;
}

const WalletAnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const { 
    walletBalance, 
    recentTransactions, 
    walletStats, 
    fetchTransactions,
    isLoadingTransactions,
    withdrawFunds,
    currentPage,
    totalPages,
    totalTransactions,
    transactionsPerPage,
    setPage
  } = useCompanyWallet();
  
  const { wallet } = useStoreSettings();
  
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date()
  });
  
  const [transactionType, setTransactionType] = useState<WalletTransactionType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<WalletTransactionStatus | 'all'>('all');
  const [withdrawAmount, setWithdrawAmount] = useState<string>('');
  const [isWithdrawing, setIsWithdrawing] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'analytics'>('overview');
  const [localFilteredTransactions, setLocalFilteredTransactions] = useState<WalletTransaction[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortField, setSortField] = useState<keyof WalletTransaction>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // حساب الرصيد من المحفظة إذا كان موجوداً
  const displayBalance = wallet?.balance ? parseFloat(wallet.balance) : walletBalance;

  // حساب الإحصائيات المتقدمة
  const calculateAdvancedStats = useCallback(() => {
    if (!recentTransactions || recentTransactions.length === 0) {
      return {
        todayRevenue: 0,
        weeklyRevenue: 0,
        monthlyRevenue: 0,
        totalTransactions: 0,
        successfulTransactions: 0,
        failedTransactions: 0,
        pendingTransactions: 0,
        totalWithdrawals: 0,
        averageTransaction: 0,
        highestTransaction: 0,
        transactionByType: {} as Record<WalletTransactionType, number>,
        successRate: 0,
        totalRevenue: walletStats?.totalRevenue || 0,
        pendingBalance: walletStats?.pendingBalance || 0
      };
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    let todayRevenue = 0;
    let weeklyRevenue = 0;
    let monthlyRevenue = 0;
    let totalWithdrawals = 0;
    let successfulTransactions = 0;
    let failedTransactions = 0;
    let pendingTransactions = 0;
    let totalAmount = 0;
    let highestTransaction = 0;
    const transactionByType: Record<WalletTransactionType, number> = {
      'send': 0,
      'collect': 0,
      'transfer': 0,
      'withdraw': 0,
      'refund': 0
    };

    recentTransactions.forEach(tx => {
      const txDate = new Date(tx.created_at);
      const amount = parseFloat(tx.amount);
      
      // عد المعاملات حسب النوع
      transactionByType[tx.type] = (transactionByType[tx.type] || 0) + 1;
      
      // عد المعاملات حسب الحالة
      switch (tx.status) {
        case 'completed':
          successfulTransactions++;
          break;
        case 'failed':
          failedTransactions++;
          break;
        case 'pending':
          pendingTransactions++;
          break;
      }
      
      if (tx.type === 'collect') {
        totalAmount += amount;
        highestTransaction = Math.max(highestTransaction, amount);
        
        if (txDate >= today) {
          todayRevenue += amount;
        }
        
        if (txDate >= oneWeekAgo) {
          weeklyRevenue += amount;
        }
        
        if (txDate >= oneMonthAgo) {
          monthlyRevenue += amount;
        }
      }
      
      if (tx.type === 'withdraw') {
        totalWithdrawals += amount;
      }
    });

    return {
      todayRevenue,
      weeklyRevenue,
      monthlyRevenue,
      totalTransactions: recentTransactions.length,
      successfulTransactions,
      failedTransactions,
      pendingTransactions,
      totalWithdrawals,
      averageTransaction: recentTransactions.length > 0 ? totalAmount / recentTransactions.length : 0,
      highestTransaction,
      transactionByType,
      successRate: recentTransactions.length > 0 ? (successfulTransactions / recentTransactions.length) * 100 : 0,
      totalRevenue: walletStats?.totalRevenue || totalAmount,
      pendingBalance: walletStats?.pendingBalance || 0
    };
  }, [recentTransactions, walletStats]);

  const stats = calculateAdvancedStats();

  // تصفية المعاملات محلياً
  useEffect(() => {
    const filtered = recentTransactions
      .filter(tx => {
        const matchesType = transactionType === 'all' || tx.type === transactionType;
        const matchesStatus = statusFilter === 'all' || tx.status === statusFilter;
        const txDate = new Date(tx.created_at);
        let matchesDate = true;
        
        // البحث في الحقول المختلفة
        const matchesSearch = !searchQuery || 
          tx.note?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          tx.reference_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          tx.amount.toLowerCase().includes(searchQuery.toLowerCase()) ||
          tx.type.toLowerCase().includes(searchQuery.toLowerCase());
        
        return matchesType && matchesStatus && matchesDate && matchesSearch;
      })
      .sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];
        
        if (sortField === 'created_at' || sortField === 'completed_at') {
          aValue = new Date(aValue as string).getTime();
          bValue = new Date(bValue as string).getTime();
        } else if (sortField === 'amount' || sortField === 'fee' || sortField === 'net_amount') {
          aValue = parseFloat(aValue as string);
          bValue = parseFloat(bValue as string);
        }
        
        if (sortDirection === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    
    setLocalFilteredTransactions(filtered);
  }, [recentTransactions, transactionType, statusFilter, dateRange, searchQuery, sortField, sortDirection]);

  // دالة السحب
  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      alert(t('enterValidAmount', 'Please enter a valid amount'));
      return;
    }

    if (parseFloat(withdrawAmount) > (displayBalance || 0)) {
      alert(t('insufficientFunds', 'Insufficient funds'));
      return;
    }

    setIsWithdrawing(true);
    try {
      const success = await withdrawFunds(parseFloat(withdrawAmount), 'bank_transfer');
      if (success) {
        alert(t('withdrawalSuccess', 'Withdrawal request submitted successfully'));
        setWithdrawAmount('');
        await fetchTransactions(currentPage, dateRange.startDate, dateRange.endDate);
      } else {
        alert(t('withdrawalFailed', 'Withdrawal failed. Please try again.'));
      }
    } catch (error) {
      alert(t('errorOccurred', 'An error occurred. Please try again.'));
    } finally {
      setIsWithdrawing(false);
    }
  };

  // دالة لتحويل النوع إلى نص
  const getTransactionTypeText = (type: WalletTransactionType): string => {
    const typeMap: Record<WalletTransactionType, string> = {
      'send': t('send', 'Send'),
      'collect': t('collect', 'Collect'),
      'transfer': t('transfer', 'Transfer'),
      'withdraw': t('withdraw', 'Withdraw'),
      'refund': t('refund', 'Refund')
    };
    return typeMap[type] || type;
  };

  // دالة لتحويل الحالة إلى لون
  const getStatusColor = (status: WalletTransactionStatus): string => {
    const colorMap: Record<WalletTransactionStatus, string> = {
      'pending': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      'completed': 'bg-green-100 text-green-800 border border-green-200',
      'failed': 'bg-red-100 text-red-800 border border-red-200',
      'cancelled': 'bg-gray-100 text-gray-800 border border-gray-200'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800 border border-gray-200';
  };

  // دالة لتحميل التقرير
  const exportReport = () => {
    const data = {
      balance: displayBalance,
      stats: stats,
      transactions: localFilteredTransactions,
      dateRange: dateRange,
      pagination: {
        currentPage,
        totalPages,
        totalTransactions,
        transactionsPerPage
      },
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wallet-report-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // دالة البحث مع التبويق
  const handleFilterSearch = async () => {
    await fetchTransactions(1, dateRange.startDate, dateRange.endDate);
  };

  // دالة مكون التبويق
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // زر الصفحة الأولى
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => setPage(1)}
          className="px-3 py-2 border border-gray-300 rounded-l-lg hover:bg-gray-50 text-sm font-medium transition-colors"
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(
          <span key="ellipsis-start" className="px-2 py-2 text-gray-500">
            ...
          </span>
        );
      }
    }

    // أزرار الصفحات
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setPage(i)}
          className={`px-3 py-2 border-t border-b border-gray-300 text-sm font-medium transition-colors ${
            currentPage === i
              ? 'bg-blue-600 text-white border-blue-600'
              : 'hover:bg-gray-50'
          } ${i === startPage ? 'border-l' : ''} ${i === endPage ? 'border-r' : ''}`}
        >
          {i}
        </button>
      );
    }

    // زر الصفحة الأخيرة
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="ellipsis-end" className="px-2 py-2 text-gray-500">
            ...
          </span>
        );
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => setPage(totalPages)}
          className="px-3 py-2 border border-gray-300 rounded-r-lg hover:bg-gray-50 text-sm font-medium transition-colors"
        >
          {totalPages}
        </button>
      );
    }

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between mt-6 gap-4">
        <div className="text-sm text-gray-600">
          {t('showing', 'Showing')} {(currentPage - 1) * transactionsPerPage + 1} {t('to', 'to')}{' '}
          {Math.min(currentPage * transactionsPerPage, totalTransactions)} {t('of', 'of')}{' '}
          {totalTransactions} {t('transactions', 'transactions')}
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPage(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium transition-colors ${
              currentPage === 1
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-gray-50'
            }`}
          >
            <StatsIcons.chevronLeft />
          </button>
          
          <div className="flex items-center">
            {pages}
          </div>
          
          <button
            onClick={() => setPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium transition-colors ${
              currentPage === totalPages
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-gray-50'
            }`}
          >
            <StatsIcons.chevronRight />
          </button>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">{t('itemsPerPage', 'Items per page')}:</span>
          <select 
            className="px-2 py-1 border border-gray-300 rounded text-sm bg-white"
            disabled
          >
            <option value="10">10</option>
          </select>
        </div>
      </div>
    );
  };

  // دالة لفرز الجدول
  const handleSort = (field: keyof WalletTransaction) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // دالة إعادة التعيين
  const handleResetFilters = () => {
    setTransactionType('all');
    setStatusFilter('all');
    setSearchQuery('');
    setDateRange({
      startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
      endDate: new Date()
    });
    fetchTransactions(1);
  };

  useEffect(() => {
    fetchTransactions(1);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {t('walletAnalytics.title', 'Wallet Analytics & Management')}
              </h1>
              <p className="text-gray-600 mt-2">
                {t('walletAnalytics.subtitle', 'Manage your store finances and track all transactions')}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => fetchTransactions(currentPage)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center space-x-2 hover:bg-blue-700 transition-colors"
              >
                <StatsIcons.refresh />
                <span>{t('refresh', 'Refresh')}</span>
              </motion.button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-8 flex space-x-1 bg-white rounded-xl p-1 shadow-sm border border-gray-200">
            {(['overview', 'transactions', 'analytics'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === tab
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {tab === 'overview' ? t('overview', 'Overview') : 
                 tab === 'transactions' ? t('transactions', 'Transactions') : 
                 t('analytics', 'Analytics')}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'overview' && renderOverviewTab({
            t,
            stats,
            displayBalance: displayBalance || 0,
            totalTransactions,
            recentTransactions,
            getTransactionTypeText,
            getStatusColor,
          })}

          {activeTab === 'transactions' && renderTransactionsTab({
            t,
            localFilteredTransactions,
            totalTransactions,
            searchQuery,
            setSearchQuery,
            dateRange,
            setDateRange,
            handleFilterSearch,
            transactionType,
            setTransactionType,
            statusFilter,
            setStatusFilter,
            isLoadingTransactions,
            handleResetFilters,
            handleSort,
            sortField,
            sortDirection,
            renderPagination,
            getTransactionTypeText,
            getStatusColor,
            currentPage,
            totalPages,
          })}

          {activeTab === 'analytics' && renderAnalyticsTab({
            t,
            stats,
            totalTransactions,
            recentTransactions,
            localFilteredTransactions,
            displayBalance: displayBalance || 0,
            withdrawAmount,
            setWithdrawAmount,
            handleWithdraw,
            isWithdrawing,
            exportReport,
            getTransactionTypeText,
            getStatusColor,
          })}
        </motion.div>

        {/* Loading State */}
        {isLoadingTransactions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <div className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-700 font-medium">
                {t('loadingTransactions', 'Loading transactions...')}
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default WalletAnalyticsPage;